import { ToastContainer } from "react-toastify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  API_ENVIRONMENTS,
  getLoginEnvironmentOptions,
  resolveSelectableApiEnvironment,
  setApiEnvironment,
} from "../services/apiConfig";
import { refreshApiClient } from "../services/api";
import AuthService from "../services/api";
import completeLogin from "../utils/completeLogin";
import { PATH } from "../constants/paths";
import useLoginStyles from "./loginStyles";
import affordamicLogo from "../assets/img/Images/logo.svg";

const environmentOptions = getLoginEnvironmentOptions("staff");

const Login = () => {
  const { classes } = useLoginStyles();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [environment, setEnvironment] = useState(resolveSelectableApiEnvironment());
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [challengeId, setChallengeId] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [formError, setFormError] = useState("");

  const history = useNavigate();
  const { isLoggedIn, token, loginError } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    setApiEnvironment(resolveSelectableApiEnvironment());
    refreshApiClient();
  }, []);

  const handleEnvironmentChange = (e) => {
    const value = e.target.value;
    if (!value || !API_ENVIRONMENTS[value]) return;
    setApiEnvironment(value);
    refreshApiClient();
    setEnvironment(value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (twoFactorStep) {
      if (!/^\d{6}$/.test(otpCode)) {
        setFormError("Enter the 6-digit code from your authenticator app");
        return;
      }
      setLoading(true);
      try {
        const response = await AuthService.verifyTwoFactorLogin({
          challengeId,
          code: otpCode,
        });
        completeLogin(dispatch, history, response.data.data);
      } catch (error) {
        setFormError(
          error?.response?.data?.message || "Invalid authentication code"
        );
        setLoading(false);
      }
      return;
    }

    if (!username || password.length < 3) return;

    setApiEnvironment(environment);
    refreshApiClient();
    setLoading(true);

    try {
      const response = await AuthService.login({ username, password });
      const payload = response?.data?.data;

      if (payload?.requiresTwoFactor) {
        setChallengeId(payload.challengeId);
        setTwoFactorStep(true);
        setOtpCode("");
        setLoading(false);
        return;
      }

      completeLogin(dispatch, history, payload);
      setLoading(false);
    } catch (error) {
      setFormError(error?.response?.data?.message || "Login failed");
      setLoading(false);
    }
  };

  useEffect(() => {
    const userToken = localStorage.getItem("user_token");
    const userRole = localStorage.getItem("user_role");
    if (isLoggedIn) {
      if (userToken != null) {
        if (userRole === "admin") {
          history("/admin/dashboard", {
            replace: true,
            state: { token },
          });
        } else if (
          userRole === "content_feeder" ||
          userRole === "content_validator"
        ) {
          history("/cm/dashboard", { replace: true });
        } else if (userRole === "partner") {
          history(PATH.PARTNER_DASHBOARD, { replace: true });
        }
      }
    }
    if (loginError) {
      setLoading(false);
    }
  }, [isLoggedIn, loginError, history, token]);

  const displayError = formError || loginError;

  return (
    <div className={classes.root}>
      <div className={classes.brandPanel}>
        <div>
          <img
            src={affordamicLogo}
            alt="Affordamic Logo"
            className={classes.brandLogo}
          />
          <h1 className={classes.brandTitle}>CMS Portal</h1>
          <p className={classes.brandSubtitle}>
            Manage exams, content, and operations from one secure workspace.
          </p>
        </div>
      </div>

      <div className={classes.formPanel}>
        <div className={classes.formCard}>
          <h2 className={classes.heading}>
            {twoFactorStep ? "Two-factor authentication" : "Welcome back"}
          </h2>
          <p className={classes.subheading}>
            {twoFactorStep
              ? "Enter the 6-digit code from your authenticator app"
              : "Sign in with your employee credentials"}
          </p>

          <form onSubmit={onSubmit}>
            {!twoFactorStep ? (
              <>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Username or email</label>
                  <input
                    type="text"
                    placeholder="you@affordamic.com"
                    className={classes.input}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>

                <div className={classes.formGroup}>
                  <label className={classes.label}>Password</label>
                  <div className={classes.passwordWrapper}>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className={classes.input}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className={classes.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div className={classes.formGroup}>
                  <label className={classes.label}>Login as</label>
                  <select
                    className={classes.select}
                    value={environment}
                    onChange={handleEnvironmentChange}
                  >
                    {environmentOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <div className={classes.formGroup}>
                <label className={classes.label}>Authentication code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="123456"
                  className={classes.input}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  required
                  autoFocus
                />
              </div>
            )}

            {displayError && (
              <div className={classes.errorBox}>
                <span>{displayError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={classes.submitButton}
            >
              {isLoading
                ? twoFactorStep
                  ? "Verifying..."
                  : "Signing in..."
                : twoFactorStep
                ? "Verify code"
                : "Sign in"}
            </button>

            {twoFactorStep ? (
              <button
                type="button"
                className={classes.submitButton}
                style={{ marginTop: 12, background: "#f3f4f6", color: "#374151" }}
                onClick={() => {
                  setTwoFactorStep(false);
                  setChallengeId("");
                  setOtpCode("");
                  setFormError("");
                }}
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                className={classes.partnerLink}
                onClick={() => history(PATH.PARTNER_LOGIN)}
              >
                Login as partner
              </button>
            )}
          </form>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Login;
